import type { CitationOutput } from "@/components/tool-kit/source-citation-builder";
import {
  apaAuthors,
  apaInTextAuthors,
  apaNameInitialsFirst,
  type CitationParts,
  chicagoBibliographyAuthors,
  chicagoNoteAuthors,
  chicagoShortAuthors,
  mlaAuthors,
  mlaDate,
  mlaInTextAuthors,
  type PartialDate,
  type PersonName,
  parsePartialDate,
  tidyParts,
  withPeriod,
} from "@/lib/tool-kit/citation-parts";
import {
  capitalizeSubtitle,
  closer,
  get,
  italic,
  mlaUrl,
  plain,
  titleCase,
} from "@/lib/tool-kit/legal-sources/common";
import {
  apaAmpersandList,
  beforeSubtitle,
  chicagoShortTitle,
  longDate,
  type MediaLabels,
  type MediaValues,
  parsePeople,
} from "@/lib/tool-kit/media-sources/common";

/*
 * Webinars — a recording you watched, or a live session you attended — in APA 7, MLA 9
 * and Chicago 18.
 *
 * Sources checked (October 2026):
 * - APA 7, Publication Manual §10.12, recorded webinar: presenter, year, italic title,
 *   "[Webinar]", the host as publisher, URL — "Yule, A. (2018). Recovery support for
 *   young people with opioid use disorders [Webinar]. American Psychiatric
 *   Association." (as reproduced by the University of Lethbridge's APA 7 guide). APA
 *   Style, "Personal communications" (§8.9): unrecorded webinars cannot be retrieved,
 *   so they are cited in the text only — "(T. Nguyen, personal communication,
 *   February 24, 2020)" — with no reference-list entry.
 * - MLA 9: MLA Style Center, "How do I cite a webinar?" — live: "Gibson, Angela. MLA
 *   Style 101. Modern Language Association, 22 Aug. 2017. Webinar."; recording: "Gibson,
 *   Angela. MLA Style 101. Modern Language Association, 30 Aug. 2017,
 *   outreach.mla.org/mla-style." The title is styled like a course title: no italics,
 *   no quotation marks. A hosting site that is not the publisher is a second container
 *   (as in the MLA's audiobook and live-stream posts).
 * - Chicago 18: CMOS has no webinar example (Chicago Style Q&A: "CMOS is silent on
 *   webinar titles"). The generator follows the official CMOS 18 Quick Guide sample for
 *   a recorded virtual lecture (§14.167): "Eric Oliver, “Why So Many Americans Believe
 *   in So Many ‘Crazy’ Things,” moderated by Andrew McCall, virtual lecture, February
 *   23, 2022, posted March 21, 2022, by University of Chicago, YouTube, 1:01:45,
 *   https://youtu.be/hfq7AnCF5bg." Shortened note: "Oliver, “Why.”" A live session
 *   follows §14.115 (talks presented at meetings): title in quotation marks, a
 *   description, the sponsor and the date.
 */

export const WEBINAR_FIELDS = {
  presenters: "presenters",
  title: "title",
  host: "host",
  date: "date",
  postedDate: "postedDate",
  moderator: "moderator",
  platform: "platform",
  duration: "duration",
  url: "url",
} as const;

const F = WEBINAR_FIELDS;

/** A recording you watched, or a live session nobody can replay. */
export type WebinarKind = "recording" | "live";

export interface WebinarLabels extends MediaLabels {
  apaNote: string;
  apaLiveNote: string;
  mlaNote: string;
  chicagoNote: string;
}

interface Webinar {
  kind: WebinarKind;
  presenters: PersonName[];
  title: string;
  host: string;
  date: PartialDate | null;
  posted: PartialDate | null;
  moderator: string;
  platform: string;
  duration: string;
  url: string;
}

function readWebinar(kind: WebinarKind, values: MediaValues): Webinar {
  return {
    kind,
    presenters: parsePeople(get(values, F.presenters)),
    title: capitalizeSubtitle(get(values, F.title)),
    host: get(values, F.host),
    date: parsePartialDate(get(values, F.date)),
    posted:
      kind === "recording" ? parsePartialDate(get(values, F.postedDate)) : null,
    moderator: get(values, F.moderator),
    platform: kind === "recording" ? get(values, F.platform) : "",
    duration: kind === "recording" ? get(values, F.duration) : "",
    url: kind === "recording" ? get(values, F.url) : "",
  };
}

const RE_ENDS_IN_MARK = /[?!]$/;

/** “Title,” — or “Title?” with no comma when the title ends in its own mark. */
function quotedTitle(title: string, mark: "," | "."): string {
  return RE_ENDS_IN_MARK.test(title) ? `“${title}”` : `“${title}${mark}”`;
}

function capitalizeFirst(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/* ---------------------------------------------------------------------- APA */

function apaYear(webinar: Webinar): string {
  const date = webinar.date ?? webinar.posted;
  return date ? String(date.year) : "n.d.";
}

function apaReference(webinar: Webinar): CitationParts {
  const publisher = webinar.host || webinar.platform;
  const tail = [
    publisher ? `${publisher}${closer(publisher)}` : "",
    webinar.url,
  ].filter(Boolean);
  const source = tail.length > 0 ? ` ${tail.join(" ")}` : "";
  const after = ` [Webinar].${source}`;
  const date = `(${apaYear(webinar)}).`;
  if (webinar.presenters.length === 0) {
    return tidyParts([
      italic(webinar.title),
      plain(` [Webinar]. ${date}${source}`),
    ]);
  }
  const authors = apaAuthors(webinar.presenters);
  return tidyParts([
    plain(`${authors}${closer(authors)} ${date} `),
    italic(webinar.title),
    plain(after),
  ]);
}

/** APA §8.9: "A. Gibson (personal communication, August 22, 2017)". */
function apaPersonalCommunication(
  webinar: Webinar,
  narrative: boolean
): CitationParts {
  const who = apaAmpersandList(webinar.presenters.map(apaNameInitialsFirst));
  const when = longDate(webinar.date) || "n.d.";
  if (narrative) {
    return [plain(`${who} (personal communication, ${when})`)];
  }
  return [plain(`(${who}, personal communication, ${when})`)];
}

function apaOutput(webinar: Webinar, labels: WebinarLabels): CitationOutput {
  if (webinar.kind === "live" && webinar.presenters.length > 0) {
    return {
      key: "apa",
      label: labels.apa,
      entries: [
        {
          label: labels.parenthetical,
          parts: apaPersonalCommunication(webinar, false),
        },
        {
          label: labels.narrative,
          parts: apaPersonalCommunication(webinar, true),
        },
      ],
      note: labels.apaLiveNote,
    };
  }
  const year = apaYear(webinar);
  const short = beforeSubtitle(titleCase(webinar.title));
  const hasPresenter = webinar.presenters.length > 0;
  return {
    key: "apa",
    label: labels.apa,
    entries: [
      { label: labels.referenceList, parts: apaReference(webinar) },
      {
        label: labels.parenthetical,
        parts: hasPresenter
          ? [plain(`(${apaInTextAuthors(webinar.presenters)}, ${year})`)]
          : [plain("("), italic(short), plain(`, ${year})`)],
      },
      {
        label: labels.narrative,
        parts: hasPresenter
          ? [plain(`${apaInTextAuthors(webinar.presenters, true)} (${year})`)]
          : [italic(short), plain(` (${year})`)],
      },
    ],
    note: webinar.kind === "live" ? labels.apaLiveNote : labels.apaNote,
  };
}

/* ---------------------------------------------------------------------- MLA */

function mlaWorksCited(webinar: Webinar): CitationParts {
  const title = titleCase(webinar.title);
  const date = mlaDate(webinar.posted ?? webinar.date);
  const elements = [
    webinar.moderator ? `moderated by ${webinar.moderator}` : "",
    webinar.host,
    date,
    webinar.url && !webinar.platform ? mlaUrl(webinar.url) : "",
  ].filter(Boolean);
  const parts: CitationParts = [];
  if (webinar.presenters.length > 0) {
    const authors = mlaAuthors(webinar.presenters);
    parts.push(plain(`${authors}${closer(authors)} `));
  }
  parts.push(plain(`${title}${closer(title)}`));
  if (elements.length > 0) {
    parts.push(plain(` ${withPeriod(capitalizeFirst(elements.join(", ")))}`));
  }
  if (webinar.platform) {
    const location = webinar.url ? `, ${mlaUrl(webinar.url)}.` : ".";
    parts.push(plain(" "), italic(webinar.platform), plain(location));
  }
  if (webinar.kind === "live") {
    parts.push(plain(" Webinar."));
  }
  return tidyParts(parts);
}

function mlaOutput(webinar: Webinar, labels: WebinarLabels): CitationOutput {
  const inText =
    webinar.presenters.length > 0
      ? mlaInTextAuthors(webinar.presenters)
      : beforeSubtitle(titleCase(webinar.title));
  return {
    key: "mla",
    label: labels.mla,
    entries: [
      { label: labels.worksCited, parts: mlaWorksCited(webinar) },
      { label: labels.inText, parts: [plain(`(${inText})`)] },
    ],
    note: labels.mlaNote,
  };
}

/* ------------------------------------------------------------------ Chicago */

/** "posted August 30, 2017, by Modern Language Association" — or the host alone. */
function chicagoPosting(webinar: Webinar): string[] {
  if (webinar.posted) {
    const by = webinar.host ? `, by ${webinar.host}` : "";
    return [`posted ${longDate(webinar.posted)}${by}`];
  }
  return [];
}

function chicagoNote(webinar: Webinar, title: string): CitationParts {
  const elements = [
    webinar.moderator ? `moderated by ${webinar.moderator}` : "",
    "webinar",
    webinar.posted ? "" : webinar.host,
    longDate(webinar.date),
    ...chicagoPosting(webinar),
    webinar.platform,
    webinar.duration,
    webinar.url,
  ].filter(Boolean);
  const lead =
    webinar.presenters.length > 0
      ? `${chicagoNoteAuthors(webinar.presenters)}, `
      : "";
  return [
    plain(
      `${lead}${quotedTitle(title, ",")} ${withPeriod(elements.join(", "))}`
    ),
  ];
}

function chicagoShortNote(webinar: Webinar, title: string): CitationParts {
  const short = quotedTitle(chicagoShortTitle(title), ".");
  if (webinar.presenters.length === 0) {
    return [plain(short)];
  }
  return [plain(`${chicagoShortAuthors(webinar.presenters)}, ${short}`)];
}

function chicagoBibliography(webinar: Webinar, title: string): CitationParts {
  const sentences: string[] = [];
  if (webinar.presenters.length > 0) {
    sentences.push(withPeriod(chicagoBibliographyAuthors(webinar.presenters)));
  }
  sentences.push(quotedTitle(title, "."));
  if (webinar.moderator) {
    sentences.push(`Moderated by ${webinar.moderator}.`);
  }
  const event = ["Webinar", webinar.posted ? "" : webinar.host];
  event.push(longDate(webinar.date));
  sentences.push(withPeriod(event.filter(Boolean).join(", ")));
  for (const posting of chicagoPosting(webinar)) {
    sentences.push(withPeriod(capitalizeFirst(posting)));
  }
  const medium = [webinar.platform, webinar.duration].filter(Boolean);
  if (medium.length > 0) {
    sentences.push(withPeriod(medium.join(", ")));
  }
  if (webinar.url) {
    sentences.push(`${webinar.url}.`);
  }
  return [plain(sentences.join(" "))];
}

function chicagoOutput(
  webinar: Webinar,
  labels: WebinarLabels
): CitationOutput {
  const title = titleCase(webinar.title);
  return {
    key: "chicago",
    label: labels.chicago,
    entries: [
      { label: labels.footnote, parts: chicagoNote(webinar, title) },
      { label: labels.shortNote, parts: chicagoShortNote(webinar, title) },
      {
        label: labels.bibliography,
        parts: chicagoBibliography(webinar, title),
      },
    ],
    note: labels.chicagoNote,
  };
}

export function webinarIsReady(values: MediaValues): boolean {
  return (
    get(values, F.title) !== "" &&
    (get(values, F.presenters) !== "" || get(values, F.host) !== "")
  );
}

export function formatWebinar(
  kind: WebinarKind,
  values: MediaValues,
  labels: WebinarLabels
): CitationOutput[] {
  const webinar = readWebinar(kind, values);
  return [
    apaOutput(webinar, labels),
    mlaOutput(webinar, labels),
    chicagoOutput(webinar, labels),
  ];
}
